import { cn } from '@/lib/cn';
import { type JSX, Show, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Motion, Presence } from 'solid-motionone';

/** Exit transition is 0.2s; hold slightly longer so unmount lands after it. */
const MODAL_EXIT_MS = 250;

/**
 * Holds the last non-null value of `source` for `ms` after it goes null, so a
 * caller that mounts a Modal behind `<Show>` can keep it mounted through the
 * exit animation: drive `open` from `source` directly (flips false at once)
 * and the `<Show>` from the returned accessor (unmounts after the hold).
 */
export function createExitHold<T>(
  source: () => T | null | undefined,
  ms = MODAL_EXIT_MS,
): () => T | null {
  const [held, setHeld] = createSignal<T | null>(null);
  createEffect(() => {
    const v = source();
    if (v != null) {
      setHeld(() => v);
    } else {
      const t = setTimeout(() => setHeld(null), ms);
      onCleanup(() => clearTimeout(t));
    }
  });
  return held;
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: JSX.Element;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnBackdrop?: boolean;
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl',
} as const;

export const Modal = (props: ModalProps): JSX.Element => {
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.open) props.onClose();
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  return (
    <Portal>
      <Presence exitBeforeEnter>
        <Show when={props.open}>
          <Motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            class="fixed inset-0 z-50 flex items-center justify-center"
          >
            <div
              class="absolute inset-0 bg-black/50"
              onClick={() => {
                if (props.closeOnBackdrop !== false) props.onClose();
              }}
              role="presentation"
            />
            <Motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
              class={cn(
                'relative mx-4 max-h-[90vh] w-full overflow-y-auto rounded-xl bg-surface p-6 shadow-xl dark:bg-surface-dark',
                SIZES[props.size ?? 'md'],
              )}
              role="dialog"
              aria-modal="true"
              aria-label={props.title}
            >
              <Show when={props.title}>
                <h2 class="mb-4 text-lg font-semibold">{props.title}</h2>
              </Show>
              {props.children}
            </Motion.div>
          </Motion.div>
        </Show>
      </Presence>
    </Portal>
  );
};
